import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Settings } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { SegmentedControl } from '../../../reusable';
import { buildFormStyles, money } from '../treasury/treasuryUi';
import { exportBudgetXlsx, updateBudget } from '../../../../services/budgetAPI';

const VIEW_ITEMS = [
  { key: 'tree', label: 'Jerárquica' },
  { key: 'margins', label: 'Márgenes' },
];

const ROW_TONES = {
  sales: { bg: '#4db6ac', color: '#fff' },
  product_margin: { bg: '#5ba3d9', color: '#fff' },
  gross: { bg: '#8aa0c4', color: '#fff' },
  contribution: { bg: '#e8a070', color: '#fff' },
  opex: { bg: '#8b8f96', color: '#fff' },
  ebitda: { bg: '#c43b9a', color: '#fff' },
};

const formatCell = (row, value) => {
  if (row.value_kind === 'percent') {
    if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toLocaleString('es-CL', { maximumFractionDigits: 1 })}%`;
  }
  if (row.value_kind === 'quantity') {
    if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString('es-CL', { maximumFractionDigits: 1 });
  }
  return money(value);
};

const visibleRows = (nodes = [], expanded) => {
  const rows = [];
  const walk = (list) => {
    (list || []).forEach((node) => {
      const children = node.children || [];
      const canExpand = children.length > 0;
      rows.push({ ...node, canExpand, expanded: canExpand && expanded.has(node.key) });
      if (canExpand && expanded.has(node.key)) walk(children);
    });
  };
  walk(nodes);
  return rows;
};

const lastActiveMonth = (nodes = []) => {
  let last = 0;
  const walk = (list) => {
    (list || []).forEach((node) => {
      (node.months || []).forEach((value, index) => {
        if (Math.abs(Number(value) || 0) > 0.004) last = Math.max(last, index + 1);
      });
      (node.actual_months || []).forEach((value, index) => {
        if (Math.abs(Number(value) || 0) > 0.004) last = Math.max(last, index + 1);
      });
      walk(node.children);
    });
  };
  walk(nodes);
  return last;
};

const axisLabels = (monthAxis = []) =>
  (monthAxis || []).map((item) => item?.label || item?.short_label || `${item?.month || ''}/${item?.year || ''}`);

const yearOptionsFor = (budgetYear, extraYears = []) => {
  const years = new Set();
  const base = Number(budgetYear) || new Date().getFullYear();
  for (let year = base - 5; year <= base + 5; year += 1) years.add(year);
  extraYears.forEach((year) => {
    const value = Number(year);
    if (value) years.add(value);
  });
  return Array.from(years).sort((a, b) => a - b);
};

const BudgetStatementTable = ({
  statement = [],
  ui,
  throughMonth = 12,
  monthCount: monthCountProp,
  monthAxis = [],
  budget = null,
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const labels = axisLabels(monthAxis);
  const monthCount = Math.max(
    1,
    labels.length || Number(monthCountProp || 0) || Math.max(Number(throughMonth || 12), lastActiveMonth(statement), 1)
  );
  const headers = labels.length
    ? labels
    : Array.from({ length: monthCount }, (_, index) => MONTH_LABELS[index] || String(index + 1));
  const [expanded, setExpanded] = useState(() => new Set(['ingresos', 'costos', 'gastos']));
  const [viewMode, setViewMode] = useState('tree');
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const firstAxis = monthAxis[0] || {};
  const lastAxis = monthAxis[monthAxis.length - 1] || {};
  const [form, setForm] = useState({
    start_month: String(budget?.horizon_start_month || firstAxis.month || 1),
    start_year: String(budget?.horizon_start_year || firstAxis.year || budgetYear),
    end_month: String(budget?.horizon_end_month || lastAxis.month || 12),
    end_year: String(budget?.horizon_end_year || lastAxis.year || budgetYear),
  });
  const yearOptions = yearOptionsFor(budgetYear, [
    form.start_year,
    form.end_year,
    firstAxis.year,
    lastAxis.year,
    budget?.horizon_start_year,
    budget?.horizon_end_year,
  ]);
  const periodCaption =
    monthAxis.length > 1
      ? `${monthAxis[0].label} – ${monthAxis[monthAxis.length - 1].label}`
      : monthAxis[0]?.label || '';

  const openConfig = () => {
    setForm({
      start_month: String(budget?.horizon_start_month || firstAxis.month || 1),
      start_year: String(budget?.horizon_start_year || firstAxis.year || budgetYear),
      end_month: String(budget?.horizon_end_month || lastAxis.month || 12),
      end_year: String(budget?.horizon_end_year || lastAxis.year || budgetYear),
    });
    setError('');
    setShowConfig(true);
  };

  const handleExport = async () => {
    if (!budget?.id || exporting) return;
    setExporting(true);
    setError('');
    try {
      await exportBudgetXlsx(budget.id, { through_month: throughMonth });
    } catch (err) {
      setError(err.message || 'No se pudo exportar el Excel.');
    } finally {
      setExporting(false);
    }
  };

  const handleSaveHorizon = async () => {
    if (!budget?.id || !canEdit) return;
    setSaving(true);
    setError('');
    try {
      const next = await updateBudget(budget.id, {
        horizon_start_month: Number(form.start_month),
        horizon_start_year: Number(form.start_year),
        horizon_end_month: Number(form.end_month),
        horizon_end_year: Number(form.end_year),
      });
      setShowConfig(false);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el periodo.');
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => {
    if (viewMode === 'margins') return budget?.margins_statement || [];
    return visibleRows(statement, expanded);
  }, [viewMode, budget?.margins_statement, statement, expanded]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const th = {
    textAlign: 'right',
    padding: '8px 6px',
    borderBottom: `1px solid ${ui.border}`,
    fontSize: 11,
    color: ui.muted,
    whiteSpace: 'nowrap',
    background: ui.header,
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 12px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Estado de resultado</h3>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: ui.muted }}>
            {periodCaption
              ? `Periodo proyectado: ${periodCaption}`
              : viewMode === 'margins'
                ? 'Ventas, costos y márgenes en cascada.'
                : 'Expandir ingresos, costos y gastos para ver el detalle por tipo y cuenta.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <SegmentedControl
            ariaLabel="Vista del estado de resultado"
            items={VIEW_ITEMS}
            activeKey={viewMode}
            onChange={setViewMode}
          />
          <button type="button" style={styles.ghostBtn} onClick={handleExport} disabled={exporting || !budget?.id}>
            <Download size={14} /> {exporting ? 'Exportando…' : 'Exportar Excel'}
          </button>
          <button type="button" style={styles.ghostBtn} onClick={openConfig}>
            <Settings size={14} /> Configuración
          </button>
        </div>
      </div>
      {error && !showConfig ? (
        <p style={{ margin: '0 16px 10px', color: ui.danger, fontSize: 12 }}>{error}</p>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 240 + headers.length * 92 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', minWidth: 240 }}>Concepto</th>
              {headers.map((label, index) => (
                <th key={`${label}-${index}`} style={th}>
                  {label}
                </th>
              ))}
              <th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isResult = viewMode === 'tree' && row.key === 'resultado';
              const isTop = viewMode === 'tree' && row.level === 0;
              const isQuantity = row.value_kind === 'quantity';
              const tone = ROW_TONES[row.tone] || null;
              const rowBg = tone?.bg || (isResult ? ui.header : 'transparent');
              const rowColor = tone?.color || (isQuantity ? ui.muted : ui.text);
              const stickyBg = tone?.bg || (isResult ? ui.header : ui.panel);
              const highlighted = Boolean(tone) || isTop || isResult;
              return (
                <tr
                  key={row.key}
                  style={{
                    background: rowBg,
                    color: rowColor,
                    borderTop: isResult ? `1px solid ${ui.border}` : 'none',
                  }}
                >
                  <td style={{ padding: '7px 8px', position: 'sticky', left: 0, background: stickyBg }}>
                    <button
                      type="button"
                      onClick={() => row.canExpand && toggle(row.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: 'transparent',
                        color: rowColor,
                        cursor: row.canExpand ? 'pointer' : 'default',
                        paddingLeft: viewMode === 'margins' ? (isQuantity ? 24 : 8) : 4 + (row.level || 0) * 16,
                        fontSize: isTop || tone ? 13 : 12,
                        fontWeight: highlighted ? 800 : row.level === 1 ? 700 : 500,
                      }}
                    >
                      {viewMode === 'tree' && row.canExpand ? (
                        row.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                      ) : viewMode === 'tree' ? (
                        <span style={{ width: 14 }} />
                      ) : null}
                      {row.label}
                    </button>
                  </td>
                  {headers.map((_, index) => (
                    <td key={`${row.key}-m${index}`} style={{ padding: '7px 6px', textAlign: 'right', fontSize: 12, fontWeight: highlighted ? 700 : 400, color: rowColor }}>
                      {formatCell(row, (row.months || [])[index])}
                    </td>
                  ))}
                  <td style={{ padding: '7px 6px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: rowColor }}>
                    {formatCell(row, row.budget_total)}
                  </td>
                </tr>
              );
            })}
            {!rows.length ? (
              <tr>
                <td colSpan={headers.length + 2} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Aún no hay ingresos ni costos proyectados.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {showConfig ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(15, 23, 42, 0.45)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setShowConfig(false)}
        >
          <div
            style={{
              width: 420,
              maxWidth: '100%',
              background: ui.panel,
              border: `1px solid ${ui.border}`,
              borderRadius: 14,
              padding: 20,
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
              display: 'grid',
              gap: 14,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Configuración</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
                Elige el periodo para el cual se proyecta este presupuesto.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={styles.label}>
                Desde
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                  <select
                    value={form.start_month}
                    onChange={(event) => setForm((prev) => ({ ...prev, start_month: event.target.value }))}
                    style={styles.input}
                  >
                    {MONTH_LABELS.map((label, index) => (
                      <option key={`start-${label}`} value={String(index + 1)}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={form.start_year}
                    onChange={(event) => setForm((prev) => ({ ...prev, start_year: event.target.value }))}
                    style={styles.input}
                  >
                    {yearOptions.map((year) => (
                      <option key={`start-year-${year}`} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label style={styles.label}>
                Hasta
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                  <select
                    value={form.end_month}
                    onChange={(event) => setForm((prev) => ({ ...prev, end_month: event.target.value }))}
                    style={styles.input}
                  >
                    {MONTH_LABELS.map((label, index) => (
                      <option key={`end-${label}`} value={String(index + 1)}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={form.end_year}
                    onChange={(event) => setForm((prev) => ({ ...prev, end_year: event.target.value }))}
                    style={styles.input}
                  >
                    {yearOptions.map((year) => (
                      <option key={`end-year-${year}`} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            </div>
            {error ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{error}</p> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={styles.ghostBtn} onClick={() => setShowConfig(false)}>
                Cancelar
              </button>
              {canEdit ? (
                <button type="button" style={styles.primaryBtn} onClick={handleSaveHorizon} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar periodo'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BudgetStatementTable;
